import React, { useRef, useEffect } from 'react';
import './cybersecurity.css';
import { Shield, Users, Eye, BarChart3, FileText, ArrowRight } from 'lucide-react';
import Cyber from './humanrisk.png';
import security from './getcertified.png';

const CyberSecurity = () => {
  const scrollToSection = () => {
    const section = document.getElementById('target-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToContact = () => {
    window.location.href = '/contact';
  };

  const goToTrustmark = () => {
    window.location.href = '/contact';
  };

  const overviewTextRef = useRef(null);
  const serviceRefs = useRef([]);
  const riskMgmtTextRef = useRef(null);
  const riskMgmtImageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('in-view');
        });
      },
      { threshold: 0.3 }
    );

    const elements = [
      overviewTextRef.current,
      riskMgmtTextRef.current,
      riskMgmtImageRef.current,
      ...serviceRefs.current
    ].filter(Boolean);

    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const services = [
    {
      icon: Shield,
      title: 'Human Risk Assessment',
      description: 'Evaluate and identify human vulnerabilities that could lead to cyber threats.'
    },
    {
      icon: Users,
      title: 'Employee Awareness Training',
      description: 'Empower your team with practical, hands-on cybersecurity practices.'
    },
    {
      icon: Eye,
      title: 'Insider Threat Mitigation',
      description: 'Develop strategies to prevent, detect, and respond to insider risks.'
    },
    {
      icon: BarChart3,
      title: 'Behavioral Analytics',
      description: 'Analyze employee behavior to proactively spot potential security issues.'
    },
    {
      icon: FileText,
      title: 'Culture & Policy Development',
      description: 'Foster a security-first mindset with tailored policies and culture change.'
    }
  ];

  return (
    <div className="cybersecurity-container">
      {/* Hero Section */}
      <section className="cybersecurity-hero">
        <div className="cybersecurity-hero-content">
          <h1 className="cybersecurity-hero-title">
            Human-Centric <span className="cybersecurity-hero-accent">Cyber Security</span>
          </h1>
          <p className="cybersecurity-hero-subtitle">
            Securing the Human Element: Manage Risks & Empower Your Team
          </p>
          <div className="cybersecurity-hero-buttons">
            <button className="cybersecurity-btn cybersecurity-btn-primary" onClick={scrollToSection}>
              Learn More
              <ArrowRight className="cybersecurity-btn-icon" />
            </button>
            <button className="cybersecurity-btn cybersecurity-btn-secondary" onClick={goToContact}>
              Contact Us
              <ArrowRight className="cybersecurity-btn-icon" />
            </button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="cybersecurity-overview" id="target-section">
        <div className="cybersecurity-section-wrapper">
          <div className="cybersecurity-overview-grid">
            <div className="cybersecurity-overview-image">
              <img src={Cyber} alt="Cybersecurity Human Factors" />
            </div>
            <div className="cybersecurity-overview-content" ref={overviewTextRef}>
              <h2 className="cybersecurity-overview-title">Our Human-Centric Approach</h2>
              <p className="cybersecurity-overview-description">
                We integrate human factors into every layer of your cybersecurity strategy.
                Our tailored approach helps organizations reduce risks by educating employees
                and building a resilient security culture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="cybersecurity-services">
        <div className="cybersecurity-section-wrapper">
          <div className="cybersecurity-section-header">
            <h2 className="cybersecurity-section-title">Our Services</h2>
            <p className="cybersecurity-section-subtitle">
              Comprehensive solutions designed to strengthen your human firewall
            </p>
          </div>
          
          <div className="cybersecurity-services-grid">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  className="cybersecurity-service-card"
                  ref={(el) => (serviceRefs.current[index] = el)}
                >
                  <div className="cybersecurity-service-icon">
                    <IconComponent strokeWidth={1.5} />
                  </div>
                  <h3 className="cybersecurity-service-title">{service.title}</h3>
                  <p className="cybersecurity-service-description">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Risk Management Section */}
      <section className="cybersecurity-risk-management">
        <div className="cybersecurity-section-wrapper">
          <div className="cybersecurity-risk-grid">
            <div className="cybersecurity-risk-content" ref={riskMgmtTextRef}>
              <h2 className="cybersecurity-risk-title">
                Cybersecurity Human Risk Certification
              </h2>
              <p className="cybersecurity-risk-description">
                Become a recognized expert in identifying and mitigating human-driven security threats. 
                Our certification program blends hands-on labs with real-world scenarios to validate your ability 
                to defend against social engineering, insider risks, and advanced phishing attacks.
              </p>
             
              <button
                className="cybersecurity-btn cybersecurity-btn-primary"
                onClick={goToTrustmark}
              >
                Get Certified
                <ArrowRight className="cybersecurity-btn-icon" />
              </button>
            </div>
            <div className="cybersecurity-risk-image" ref={riskMgmtImageRef}>
              <img src={security} alt="Cybersecurity Human Risk Certification" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cybersecurity-cta">
        <div className="cybersecurity-section-wrapper">
          <h2 className="cybersecurity-cta-title">Ready to Strengthen Your Security?</h2>
          <p className="cybersecurity-cta-subtitle">
            Our pricing is customized to the needs of your organization. Contact us for a personalized quote.
          </p>
          <button className="cybersecurity-btn cybersecurity-btn-primary" onClick={goToContact}>
            Contact Us
            <ArrowRight className="cybersecurity-btn-icon" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default CyberSecurity;